import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useIsV5Mobile } from "../../hooks/useMediaQuery";
import "./LoomTaskWorkspace.css";

export type LoomTask = "details" | "propulsor" | "section";
export type LoomControlSide = "port" | "starboard" | "both";

export interface LoomTaskSelectionContext {
  readonly controlSide: LoomControlSide | null;
  readonly controlSurfaceId: string | null;
  readonly nodeId: string | null;
  readonly legacyControlPoseRevision: string | null;
  readonly workspaceRevision: string | null;
}

interface LoomTaskWorkspaceProps {
  readonly activeTask: LoomTask | null;
  readonly children: ReactNode;
  readonly details: ReactNode;
  readonly onCloseTask: () => void;
  readonly propulsor: ReactNode;
  readonly section: ReactNode;
  readonly selection: LoomTaskSelectionContext;
}

function readableId(value: string | null): string {
  if (!value) return "Loading selection";
  return value
    .replace(/[._:/-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function selectionLabel(selection: LoomTaskSelectionContext): string {
  const selected = selection.controlSurfaceId ?? selection.nodeId;
  const side = selection.controlSide && selection.controlSide !== "both"
    ? ` · ${readableId(selection.controlSide)}` : "";
  return `${readableId(selected)}${side}`;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function focusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((element) => !element.hidden && element.getAttribute("aria-hidden") !== "true")
    .filter((element) => {
      const closedDetails = element.closest("details:not([open])");
      return !closedDetails || (element.tagName === "SUMMARY" && element.parentElement === closedDetails);
    });
}

export function LoomTaskWorkspace({
  activeTask,
  children,
  details,
  onCloseTask,
  propulsor,
  section,
  selection,
}: LoomTaskWorkspaceProps) {
  const overlayId = activeTask ? `loom-task-${activeTask}` : undefined;
  const [sectionMounted, setSectionMounted] = useState(activeTask === "section");
  const [propulsorMounted, setPropulsorMounted] = useState(activeTask === "propulsor");
  const compactTaskSheet = useIsV5Mobile();
  const isolateScene = Boolean(activeTask && compactTaskSheet);
  const activeOverlayRef = useRef<HTMLElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const previousTaskRef = useRef<LoomTask | null>(null);
  const workspaceRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (activeTask === "section") setSectionMounted(true);
    if (activeTask === "propulsor") setPropulsorMounted(true);
  }, [activeTask]);

  useLayoutEffect(() => {
    const previousTask = previousTaskRef.current;
    if (activeTask) {
      if (!previousTask && document.activeElement instanceof HTMLElement) {
        openerRef.current = document.activeElement === document.body
          || document.activeElement === document.documentElement
          ? null : document.activeElement;
      }
      const overlay = activeOverlayRef.current;
      const initialFocus = overlay?.querySelector<HTMLElement>("[data-loom-task-initial-focus]");
      (initialFocus ?? overlay)?.focus();
    } else if (previousTask) {
      const opener = openerRef.current;
      const selectedTreeItem = workspaceRef.current?.querySelector<HTMLElement>(
        '[role="treeitem"][aria-selected="true"]',
      );
      if (opener?.isConnected) opener.focus();
      else (selectedTreeItem ?? workspaceRef.current)?.focus();
      openerRef.current = null;
    }
    previousTaskRef.current = activeTask;
  }, [activeTask, propulsorMounted, sectionMounted]);

  useEffect(() => {
    if (!activeTask) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      const overlay = activeOverlayRef.current;
      if (!overlay) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onCloseTask();
        return;
      }
      if (event.key !== "Tab" || !compactTaskSheet) return;
      const focusable = focusableElements(overlay);
      if (focusable.length === 0) {
        event.preventDefault();
        overlay.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const focused = document.activeElement;
      if (!overlay.contains(focused)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && focused === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && focused === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [activeTask, compactTaskSheet, onCloseTask]);

  return (
    <section
      aria-label="Loom Build"
      className="loom-task-workspace"
      data-active-task={activeTask ?? "none"}
      data-control-side={selection.controlSide ?? "none"}
      data-control-surface-id={selection.controlSurfaceId ?? "none"}
      data-legacy-control-pose-revision={selection.legacyControlPoseRevision ?? "unbound"}
      data-selected-node-id={selection.nodeId ?? "pending"}
      data-testid="loom-task-workspace"
      data-workspace-revision={selection.workspaceRevision ?? "pending"}
      ref={workspaceRef}
      tabIndex={-1}
    >
      <div
        aria-hidden={isolateScene ? "true" : undefined}
        className="loom-task-scene"
        data-testid="loom-task-scene"
        inert={isolateScene ? true : undefined}
      >{children}</div>
      {activeTask === "details" ? (
        <aside
          aria-modal={compactTaskSheet ? "true" : undefined}
          aria-label="Aircraft details"
          className="loom-task-overlay is-details"
          data-testid="loom-task-details"
          id={overlayId}
          ref={activeOverlayRef}
          role="dialog"
          tabIndex={-1}
        >
          <header className="loom-task-overlay-header">
            <div>
              <span>Aircraft</span>
              <h2>Aircraft details</h2>
            </div>
            <button
              aria-label="Close aircraft details"
              data-loom-task-initial-focus
              onClick={onCloseTask}
              type="button"
            >Close</button>
          </header>
          <div className="loom-task-overlay-content">{details}</div>
        </aside>
      ) : null}
      {sectionMounted ? <aside
        aria-modal={activeTask === "section" && compactTaskSheet ? "true" : undefined}
        aria-label="Section design"
        className="loom-task-overlay is-section"
        data-testid="loom-section-split"
        hidden={activeTask !== "section"}
        id={activeTask === "section" ? overlayId : undefined}
        ref={activeTask === "section" ? activeOverlayRef : undefined}
        role="dialog"
        tabIndex={-1}
      >
        <header className="loom-task-overlay-header">
          <div><span>{selectionLabel(selection)}</span><h2>Section design</h2></div>
          <button aria-label="Close section design" data-loom-task-initial-focus
            onClick={onCloseTask} type="button">Close</button>
        </header>
        <div className="loom-task-overlay-content">{section}</div>
      </aside> : null}
      {propulsorMounted ? <aside
        aria-modal={activeTask === "propulsor" && compactTaskSheet ? "true" : undefined}
        aria-label="Propulsor evidence"
        className="loom-task-overlay is-propulsor"
        data-testid="loom-propulsor-task"
        hidden={activeTask !== "propulsor"}
        id={activeTask === "propulsor" ? overlayId : undefined}
        ref={activeTask === "propulsor" ? activeOverlayRef : undefined}
        role="dialog"
        tabIndex={-1}
      >
        <header className="loom-task-overlay-header">
          <div><span>Adapted · session only</span><h2>Propulsor evidence</h2></div>
          <button aria-label="Close propulsor evidence" data-loom-task-initial-focus
            onClick={onCloseTask} type="button">Close</button>
        </header>
        <div className="loom-task-overlay-content">{propulsor}</div>
      </aside> : null}
    </section>
  );
}

export default LoomTaskWorkspace;
